import clsx from 'clsx'
import React, { useId, useLayoutEffect, useState } from 'react'
import { createPortal } from 'react-dom'

import { useFilledState } from 'slab/hooks/useFilledState'
import { SolidAlertTriangleIcon } from 'slab/icons/SolidAlertTriangle'
import { type IconProp } from 'slab/types'

import { InputBlockContext } from './context'
import { type ColumnSpan } from './types'

type FieldChildrenProps<T extends HTMLInputElement | HTMLSelectElement> = {
  'aria-describedby'?: string;
  'aria-invalid': boolean;
  className: string;
  id: string;
  onChange: (event: React.ChangeEvent<T>) => void;
  ref: React.RefObject<T>;
}

interface FieldProps<T extends HTMLInputElement | HTMLSelectElement> {
  children: (formFieldProps: FieldChildrenProps<T>) => React.ReactNode;
  defaultValue?: string | number | readonly string[];
  id?: string;
  error?: React.ReactNode;
  label: React.ReactNode;
  leadingIcon?: IconProp;
  onChange?: (event: React.ChangeEvent<T>) => void;
  span?: ColumnSpan;
  value?: string | number | readonly string[];
}

const columnSpanMapping: Record<ColumnSpan, string> = {
  1: 'col-span-1',
  2: 'col-span-2',
  3: 'col-span-3',
  4: 'col-span-4',
  5: 'col-span-5',
  6: 'col-span-6',
}

function usePortalElement () {
  const { portalId } = React.useContext(InputBlockContext)

  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(
    document.getElementById(portalId)
  )

  /**
   * If the portal container is created by an ancestor React component, it may
   * not be present in the DOM when this component first mounts. We'll use a
   * layout effect to check for it one more time before this component is
   * first painted.
   */
  useLayoutEffect(() => {
    if (portalContainer) return

    setPortalContainer(document.getElementById(portalId))
  }, [portalId])

  return portalContainer
}

export function InputBlockField<
  T extends HTMLInputElement | HTMLSelectElement
> ({ children, error, id, label, leadingIcon: LeadingIcon, span, ...props }: FieldProps<T>) {
  const { filled, handleChange, ref } = useFilledState(
    props.defaultValue,
    props.value,
    props.onChange
  )

  const autogeneratedId = useId()
  const errorMessageId = useId()
  const inputId = id || autogeneratedId
  const portalContainer = usePortalElement()

  const columnSpanClassName = span ? columnSpanMapping[span] : undefined

  function renderChildren () {
    const className = clsx(
      'appearance-none opacity-100 bg-none bg-transparent focus:outline-hidden focus:ring-transparent', // reset some global styles
      'border-none hover:border-none focus:border-none focus-visible:border-none',
      'w-full h-full',
      'body leading-[1.375rem]',
      'placeholder:text-transparent focus:placeholder:text-gray-500 placeholder:transition-none focus:placeholder:transition placeholder:delay-75 focus:placeholder:delay-0',
      'pr-2 pt-6 pb-2',
      'peer field',
      {
        'text-gray-800 focus-within:text-gray-800 aria-invalid:text-red-700 aria-invalid:focus-within:text-gray-800': filled,
        'text-transparent transition-none focus:transition delay-75 focus:delay-0 focus-within:text-gray-500  [&::-webkit-calendar-picker-indicator]:invisible [&:focus-within::-webkit-calendar-picker-indicator]:visible': !filled,
        'pl-2': !LeadingIcon,
        'pl-8': LeadingIcon,
      }
    )

    return children({
      ...props,
      'aria-describedby': error ? errorMessageId : undefined,
      'aria-invalid': !!error,
      className,
      id: inputId,
      onChange: handleChange,
      ref,
    })
  }

  function renderError () {
    if (!error || typeof error === 'boolean' || !portalContainer) return null

    const errorElement = (
      <div className='flex gap-x-1 mt-1' id={errorMessageId}>
        <div className='text-red-400'>
          <SolidAlertTriangleIcon aria-hidden size={16} />
        </div>
        <div className='body-sm-bold text-red-700'>
          {error}
        </div>
      </div>
    )

    return createPortal(errorElement, portalContainer)
  }

  return (
    <div
      data-filled={filled}
      data-form-field-for={inputId}
      className={clsx(
        'flex',
        columnSpanClassName,
        'has-[.field:focus]:relative has-[[aria-invalid="true"]]:relative',
        'bg-white hover:bg-gray-50 focus-within:bg-thatch-50 has-[.field:disabled]:bg-gray-100',
        'outline-gray-400 has-[.field:focus]:outline-thatch-500 has-[[aria-invalid="true"]]:outline-red-300',
        '-mb-px group-last/input-group-row:-mb-0 -mr-px last:-mr-0',
        'group-first/input-group-row:first:rounded-tl-lg group-first/input-group-row:last:rounded-tr-lg',
        'group-last/input-group-row:first:rounded-bl-lg group-last/input-group-row:last:rounded-br-lg',
        'has-[[data-popper-side="bottom"][aria-expanded="true"]]:!rounded-b-none has-[[data-popper-side="top"][aria-expanded="true"]]:!rounded-t-none',
        'outline outline-1 -outline-offset-1 has-[.field:focus]:outline-2 has-[.field:focus]:-outline-offset-2',
        'transition'
      )}
    >
      <div className='relative h-full w-full'>
        {renderChildren()}
        {LeadingIcon && (
          <div
            className={clsx(
              'absolute left-2 top-0 bottom-0',
              'pt-6 pb-2',
              'flex items-center',
              'pointer-events-none',
              'text-gray-500',
              'transition-none peer-focus-within:transition delay-75 peer-focus-within:delay-0',
              {
                'opacity-100': filled,
                'opacity-0 peer-focus-within:opacity-100': !filled,
              }
            )}
          >
            <LeadingIcon aria-hidden size={16} />
          </div>
        )}
        <label
          className={clsx(
            'absolute left-2',
            'text-gray-500 leading-4',
            'pointer-events-none transition-all ease-out duration-75',
            {
              'top-2 body-sm': filled,
              'top-5 peer-focus-within:top-2 body peer-focus-within:body-sm peer-focus-within:leading-4 peer-disabled:text-black/25':
                !filled,
            }
          )}
          htmlFor={inputId}
        >
          {label}
        </label>
      </div>
      {renderError()}
    </div>
  )
}
